import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { FileCheck, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_CHAR = { name: '', quantitative: true, target: '', lower_limit: '', upper_limit: '', unit: '', mandatory: true };
const EMPTY = { material: '', plant: '', description: '', sample_size: 1, characteristics: [{ ...NEW_CHAR }], status: 'Active' };

const InspectionPlans = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('inspection_plan');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quality/inspection-plans`, { withCredentials: true });
      setRows(res.data);
    } catch (e) { toast.error('Failed to load inspection plans'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setChar = (i, k, v) => setForm((p) => {
    const chars = [...p.characteristics];
    chars[i] = { ...chars[i], [k]: v };
    return { ...p, characteristics: chars };
  });
  const addChar = () => setForm((p) => ({ ...p, characteristics: [...p.characteristics, { ...NEW_CHAR }] }));
  const removeChar = (i) => setForm((p) => ({ ...p, characteristics: p.characteristics.filter((_, idx) => idx !== i) }));

  const openNew = () => { setForm(EMPTY); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        sample_size: parseInt(form.sample_size, 10) || 1,
        characteristics: form.characteristics.filter((c) => c.name).map((c) => ({
          name: c.name, quantitative: c.quantitative, mandatory: c.mandatory, unit: c.unit || null,
          target: c.target === '' ? null : parseFloat(c.target),
          lower_limit: c.lower_limit === '' ? null : parseFloat(c.lower_limit),
          upper_limit: c.upper_limit === '' ? null : parseFloat(c.upper_limit),
        })),
      };
      await axios.post(`${API}/quality/inspection-plans`, payload, { withCredentials: true });
      toast.success('Inspection plan created'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save plan'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="inspection-plans-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><FileCheck className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inspection Plans</h1>
              <p className="text-gray-600 text-sm mt-0.5">Quality characteristics per material.</p>
            </div>
          </div>
          {writable && <Button onClick={openNew} data-testid="new-inspection-plan-btn"><Plus className="h-4 w-4 mr-2" />New Plan</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Plan #</TableHead><TableHead>Material</TableHead><TableHead>Plant</TableHead>
              <TableHead>Description</TableHead><TableHead className="text-right">Sample</TableHead>
              <TableHead className="text-right">Characteristics</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No inspection plans yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.plan_number} data-testid={`plan-row-${r.plan_number}`}>
                    <TableCell className="font-medium">{r.plan_number}</TableCell>
                    <TableCell>{r.material}</TableCell>
                    <TableCell>{r.plant || '—'}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell className="text-right">{r.sample_size}</TableCell>
                    <TableCell className="text-right">{r.characteristics?.length || 0}</TableCell>
                    <TableCell><Badge variant={r.status === 'Active' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Inspection Plan</DialogTitle>
            <DialogDescription>Define characteristics and specification limits for a material.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-2">
            <div><Label>Material</Label><Input data-testid="plan-material" value={form.material} onChange={(e) => set('material', e.target.value)} /></div>
            <div><Label>Plant</Label><Input data-testid="plan-plant" value={form.plant} onChange={(e) => set('plant', e.target.value)} placeholder="1000" /></div>
            <div><Label>Sample Size</Label><Input type="number" min={1} data-testid="plan-sample" value={form.sample_size} onChange={(e) => set('sample_size', e.target.value)} /></div>
            <div className="col-span-3"><Label>Description</Label><Input data-testid="plan-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Characteristics</h4>
              <Button size="sm" variant="outline" onClick={addChar} data-testid="add-char-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.characteristics.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`char-row-${i}`}>
                  <Input className="col-span-3" placeholder="Name" data-testid={`char-name-${i}`} value={c.name} onChange={(e) => setChar(i, 'name', e.target.value)} />
                  <select className="col-span-2 h-9 rounded-md border border-gray-200 text-sm px-2" value={c.quantitative ? 'q' : 'a'} onChange={(e) => setChar(i, 'quantitative', e.target.value === 'q')} data-testid={`char-type-${i}`}>
                    <option value="q">Quantitative</option>
                    <option value="a">Qualitative</option>
                  </select>
                  <Input className="col-span-2" placeholder="Target" type="number" disabled={!c.quantitative} value={c.target} onChange={(e) => setChar(i, 'target', e.target.value)} />
                  <Input className="col-span-1" placeholder="Low" type="number" disabled={!c.quantitative} value={c.lower_limit} onChange={(e) => setChar(i, 'lower_limit', e.target.value)} />
                  <Input className="col-span-1" placeholder="High" type="number" disabled={!c.quantitative} value={c.upper_limit} onChange={(e) => setChar(i, 'upper_limit', e.target.value)} />
                  <Input className="col-span-2" placeholder="Unit" value={c.unit} onChange={(e) => setChar(i, 'unit', e.target.value)} />
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removeChar(i)} data-testid={`remove-char-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.material || form.characteristics.filter((c) => c.name).length === 0} data-testid="save-inspection-plan-btn">{saving ? 'Saving...' : 'Save Plan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default InspectionPlans;
